import { get } from '@shared/adapters/lodash'
import { maskAccountNumber } from '@shared/utils'
import type { BankAccountWireDto, BankAccountDetailWireDto } from './bankAccountContracts'
import type {
  BankAccount,
  BankAccountDetail,
  CreatedBankAccount,
  UploadedStatementResult,
} from '../types/bankAccountTypes'
import { PENNY_DROP_ACCOUNT_STATUS } from '../constants/pennyDropStatus'
import { SUPPORTED_BANKS } from '../constants/banks'

// The add-account dropdown stores the bank's slug ("emirates-nbd") as its
// name, and the API echoes it back; other rows carry a display name ("Emirates
// NBD"). Both normalise to the same key — the same rule the investor-app uses
// to match its bank marks — so every surface shows the display name.
const normaliseBankKey = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '')

const findSupportedBank = (name: string) => {
  const key = normaliseBankKey(name)
  return SUPPORTED_BANKS.find(
    (bank) => normaliseBankKey(bank.value) === key || normaliseBankKey(bank.label) === key,
  )
}

// Bank marks are optional assets named after the slug
// (src/assets/images/banks/emirates-nbd.png). Globbed rather than imported so
// a mark that hasn't been added yet just falls back to the initials tile.
const BANK_LOGO_FILES = import.meta.glob<string>('/src/assets/images/banks/*.{png,svg,webp}', {
  eager: true,
  import: 'default',
})
const BANK_LOGO_BY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(BANK_LOGO_FILES).map(([path, url]) => [
    path.slice(path.lastIndexOf('/') + 1, path.lastIndexOf('.')),
    url,
  ]),
)

export const toBankDisplayName = (name: string | undefined | null): string => {
  if (!name) return ''
  return findSupportedBank(name)?.label ?? name
}

export const toBankLogoSrc = (name: string | undefined | null): string | null => {
  if (!name) return null
  const slug = findSupportedBank(name)?.value
  return (slug && BANK_LOGO_BY_SLUG[slug]) || null
}

export type BankAccountDisplayStatus = 'verified' | 'pending' | 'rejected'

export type BankAccountListItemView = BankAccount & {
  maskedNumber: string
  status: BankAccountDisplayStatus
}

// Collapses the raw backend account_status enum into the three display states
// the UI renders. 'verified' is locked/approved; 'rejected' and
// 'verification_failed' both mean the bank declined it, as does a failed
// penny drop (constants/pennyDropStatus.ts); everything else (e.g.
// 'pending_verification', penny drop in progress / completed) is still under
// review.
export const toBankAccountDisplayStatus = (
  accountStatus: string | undefined,
): BankAccountDisplayStatus => {
  if (accountStatus === 'verified') return 'verified'
  if (
    accountStatus === 'rejected' ||
    accountStatus === 'verification_failed' ||
    accountStatus === PENNY_DROP_ACCOUNT_STATUS.FAILED
  ) {
    return 'rejected'
  }
  return 'pending'
}

export const parseBankAccountList = (resp: unknown): BankAccount[] => {
  const rows = (get(resp, 'data') ?? []) as BankAccountWireDto[]
  return rows.map((account) => ({
    id: account.id || '',
    bankName: toBankDisplayName(account.bankName),
    logoSrc: toBankLogoSrc(account.bankName),
    accountNumber: account.accountNumber || '',
    accountStatus: account.accountStatus || '',
    isPrimary: account.isPrimary ?? false,
    rejectionReason: account.rejectionReason ?? null,
  }))
}

export const parseBankAccountListToView = (accounts: BankAccount[]): BankAccountListItemView[] => {
  return accounts.map((account) => ({
    ...account,
    bankName: account.bankName || 'Bank Account',
    maskedNumber: maskAccountNumber(account.accountNumber),
    status: toBankAccountDisplayStatus(account.accountStatus),
  }))
}

export const parseBankAccountDetail = (resp: unknown): BankAccountDetail => {
  const data = (get(resp, 'data') ?? {}) as BankAccountDetailWireDto
  return {
    id: data.id,
    bankName: toBankDisplayName(data.bankName),
    logoSrc: toBankLogoSrc(data.bankName),
    accountHolderName: data.accountHolderName,
    accountNumber: data.accountNumber,
    iban: data.iban ?? null,
    swiftCode: data.swiftCode ?? null,
    currencyCode: data.currencyCode,
    accountStatus: data.accountStatus,
    isPrimary: data.isPrimary,
    createdAt: data.createdAt,
  }
}

export const parseCreateBankAccountResult = (resp: unknown): CreatedBankAccount => ({
  id: get(resp, 'data.id') || '',
  accountStatus: get(resp, 'data.accountStatus') || '',
})

// The upload response carries the account status once penny drop ships; today
// it may carry nothing, which the dialog treats as the current success flow.
export const parseUploadStatementResult = (resp: unknown): UploadedStatementResult => ({
  accountStatus: get(resp, 'data.accountStatus') ?? null,
})
